import { DetailedError } from '@/library/api/honoClient';

/**
 * Pull a single user-friendly message from a hono `DetailedError` thrown by
 * `parseResponse` — best for toasts / inline field errors where one line is
 * wanted.
 *
 * The backend `errorHandler` (reipm-api/src/middleware/errorHandler.ts) responds
 * with shapes like:
 *  - `{ errors: { field: string[] }, message: string }` (validation)
 *  - `{ error: string }` (not-found / permission)
 *  - `{ error: { issues: ZodIssue[] } }` (Zod)
 *  - `{ error: 'Internal server error', ... }` (500)
 *
 * hono's `parseResponse` wraps the body under `error.detail.data`.
 * Priority: first Zod issue → first field-error → `data.error` → `data.message`
 * → `err.message` → fallback.
 */
export const extractApiError = (err: unknown, fallback: string): string => {
  if (!(err instanceof DetailedError)) {
    if (err instanceof Error && err.message) return err.message;
    return fallback;
  }
  const data = (err.detail as { data?: unknown } | undefined)?.data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const zodError = obj.error;
    if (zodError && typeof zodError === 'object' && Array.isArray((zodError as { issues?: unknown }).issues)) {
      const firstIssue = (zodError as { issues: Array<{ message?: string }> }).issues[0];
      if (firstIssue?.message) return firstIssue.message;
    }
    if (obj.errors && typeof obj.errors === 'object') {
      for (const v of Object.values(obj.errors as Record<string, unknown>)) {
        if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') return v[0];
      }
    }
    if (typeof obj.error === 'string') return obj.error;
    if (typeof obj.message === 'string') return obj.message;
  }
  if (err.message) return err.message;
  return fallback;
};
